import { useCallback, useEffect, useRef, useState } from "react";
import { useDataChanged } from "../useDataChanged";
import { createPortal } from "react-dom";
import { AppShell } from "../components/AppShell";
import { OverviewTopbar, ProjectsRow } from "../components/OverviewTopbar";
import { ScanStrip } from "../components/ScanStrip";
import {
  api,
  downloadAuthenticated,
  fetchAuthenticatedText,
  isProjectVerified,
  type ReportVerificationInfo,
  type ScanListItem,
} from "../api";
import { useAppState } from "../AppStateContext";
import { IconCheck, IconDownload } from "../components/icons";
import { CardSkeleton } from "../components/Skeletons";
import { useI18n } from "../i18n";
import {
  isTerminalScanStatus,
  reportDownloadErrorKey,
  reportVerificationBadge,
  reportVerificationTooltipLines,
} from "../reportVerification";
import "./overview.css";
import "./reports.css";

type ReportFormat = "pdf" | "html" | "sarif" | "json";

// Injected into the sandboxed HTML preview. Two jobs:
//  1. Render the report at its true page width. The report is an A4 print
//     document (mm units, `@page A4`, a fixed `.cover{width:210mm}`), so in a
//     fluid iframe it "разъезжается": fixed-mm blocks (cover/banner) stay 210mm
//     (~794px) while fluid `.content` fills the wider iframe — leaving a white
//     gap and misaligning sections. Pin the sheet to 210mm and centre it so
//     every section lines up, exactly like the downloadable PDF.
//  2. Wrap long unbreakable tokens (CSV header dumps, URLs) instead of letting
//     them overflow. (The backend PDF template wraps these already.)
const REPORT_PREVIEW_CSS =
  "<style>" +
  "html{background:#2b2b2b}" +
  "body{width:210mm!important;margin:0 auto!important;background:#fff!important;overflow-wrap:anywhere}" +
  "pre,code{white-space:pre-wrap!important;word-break:break-word}" +
  "table,td,th{word-break:break-word}" +
  "</style>";

// The report is an A4 sheet (210mm ≈ 794px). We render the iframe at that fixed
// logical width and scale it to fill the modal, so on big monitors it reads
// large instead of sitting tiny in the middle (and shrinks to fit on phones).
const A4_PX = 794;

export default function ReportsPage() {
  const { projects, activeProject, setActiveProjectId } = useAppState();
  const { t, lang } = useI18n();
  const [scans, setScans] = useState<ScanListItem[]>([]);
  // Manual attestation per scan_id (planning#213). Missing entry = not loaded
  // or the request failed; the row then simply shows no attestation badge.
  const [verifications, setVerifications] = useState<Record<string, ReportVerificationInfo>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [readingScanId, setReadingScanId] = useState<string | null>(null);
  const [readingContent, setReadingContent] = useState<string>("");
  // Live size of the preview viewport, tracked so the A4 sheet scales to fill it.
  const previewBoxRef = useRef<HTMLDivElement>(null);
  const [previewBox, setPreviewBox] = useState({ w: A4_PX, h: 600 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { scans: items } = await api.listScans({});
      setScans(items);
      setLoading(false);
      // Attestation is only meaningful once a report can exist. Best-effort:
      // a failed lookup leaves the row without a badge rather than failing
      // the whole page.
      const terminal = items.filter((s) => isTerminalScanStatus(s.status));
      const results = await Promise.allSettled(
        terminal.map((s) => api.getReportVerification(s.scan_id)),
      );
      const next: Record<string, ReportVerificationInfo> = {};
      results.forEach((r, i) => {
        if (r.status === "fulfilled") next[terminal[i].scan_id] = r.value;
      });
      setVerifications(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("reports.loading"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useDataChanged(load);

  useEffect(() => {
    const id = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(id);
  }, [load]);

  const scanLabel = (s: ScanListItem): string => {
    if (s.project_id) {
      const proj = projects.find((p) => p.project_id === s.project_id);
      if (proj) return proj.name;
    }
    return s.scan_id;
  };

  const download = async (s: ScanListItem, format: ReportFormat = "pdf") => {
    setError(null);
    const url = api.scanReportUrl(s.scan_id, format);
    try {
      await downloadAuthenticated(url, `${scanLabel(s)}-report.${format}`);
    } catch (err) {
      // 409 report_not_ready: the scan reported a terminal status but the
      // server has not settled the report yet — tell the user to wait rather
      // than surfacing a bare "Conflict".
      setError(t(reportDownloadErrorKey(err)));
    }
  };

  const read = async (s: ScanListItem) => {
    setReadingScanId(s.scan_id);
    setReadingContent(t("common.loading"));
    try {
      const text = await fetchAuthenticatedText(api.scanReportUrl(s.scan_id, "html"));
      setReadingContent(text);
    } catch (err) {
      const key = reportDownloadErrorKey(err);
      const message =
        key === "reports.notReady"
          ? t(key)
          : err instanceof Error
            ? err.message
            : t("reports.error");
      setReadingContent(textToHtml(t("reports.loadFailed", { message })));
    }
  };

  // Track the preview viewport size while the modal is open (ResizeObserver
  // covers both the initial open and window resizes) so the sheet scale stays
  // correct without reloading the iframe.
  useEffect(() => {
    if (!readingScanId) return;
    const el = previewBoxRef.current;
    if (!el) return;
    const sync = () => setPreviewBox({ w: el.clientWidth, h: el.clientHeight });
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [readingScanId]);

  // Scale the A4 sheet to the viewport width, clamped so it never gets absurd
  // on ultra-wide monitors or unreadably tiny on phones.
  const previewScale = Math.min(2, Math.max(0.4, previewBox.w / A4_PX));

  const locale = lang === "ru" ? "ru-RU" : "en-US";

  return (
    <AppShell>
      <div className="overview anim-page">
        <OverviewTopbar />
        <ProjectsRow
          searches={projects.map((p) => ({
            id: p.project_id,
            label: p.name,
            verified: isProjectVerified(p),
          }))}
          activeId={activeProject?.project_id}
          onSelect={setActiveProjectId}
        />
        <ScanStrip />

        <div className="reports__header">
          <p className="overview__section-label" style={{ margin: 0 }}>
            {t("reports.sectionLabel")}
          </p>
        </div>

        {error ? <p className="auth-error" style={{ textAlign: "left" }}>{error}</p> : null}

        <div className="reports anim-stagger" style={{ ["--anim-stagger-step" as never]: "50ms" }}>
          {loading && scans.length === 0 ? (
            <CardSkeleton count={4} />
          ) : scans.length === 0 ? (
            <p className="helper">{t("reports.empty")}</p>
          ) : (
            scans.map((s, i) => {
              const ready = isTerminalScanStatus(s.status);
              const badgeLabel = ready ? t("reports.statusReady") : t("reports.statusPending");
              const when = s.completed_at ?? s.created_at;
              const verification = verifications[s.scan_id];
              const attest = verification ? reportVerificationBadge(verification) : null;
              const formatDate = (iso: string) => new Date(iso).toLocaleDateString(locale);
              const tooltip = verification
                ? reportVerificationTooltipLines(verification, formatDate)
                    .map((l) => t(l.key, l.vars))
                    .join("\n")
                : "";
              return (
                <article key={s.scan_id} className="reports__row" data-tour={i === 0 ? "report-row" : undefined}>
                  <div className="reports__info">
                    <div className="reports__title">
                      <span>{scanLabel(s)}</span>
                      <span className={`scan-badge scan-badge--${ready ? "ready" : "running"}`}>
                        {badgeLabel}
                      </span>
                      {attest ? (
                        <span
                          className={`reports__verif reports__verif--${attest.kind}`}
                          title={`${t(`reports.${attest.kind}Title`)}\n${tooltip}`}
                        >
                          {attest.kind === "verified" ? <IconCheck width={12} height={12} /> : null}
                          {t(`reports.${attest.kind}Badge`)}
                        </span>
                      ) : null}
                    </div>
                    <span className="reports__date">
                      {when ? (ready ? t("reports.finishedAt", { date: formatDate(when) }) : formatDate(when)) : "—"}
                    </span>
                  </div>
                  <div className="reports__actions">
                    <button
                      type="button"
                      className="btn--outline"
                      data-tour={i === 0 ? "report-download" : undefined}
                      disabled={!ready}
                      onClick={() => download(s, "pdf")}
                      title={t("reports.downloadTitle")}
                    >
                      <IconDownload width={14} height={14} />
                      {t("reports.download")}
                    </button>
                    <button
                      type="button"
                      className="btn--outline"
                      data-tour={i === 0 ? "report-read" : undefined}
                      disabled={!ready}
                      onClick={() => read(s)}
                    >
                      {t("reports.read")}
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>

      {readingScanId ? createPortal(
        // Portal to <body>: the .anim-page page transform forms the containing
        // block for `position: fixed`, which would offset this backdrop from the
        // viewport (same fix as ConfirmDialog).
        <div className="dialog-backdrop" onMouseDown={() => setReadingScanId(null)}>
          <div
            className="dialog dialog--report"
            role="dialog"
            style={{ width: "min(1400px, 94vw)", maxWidth: "none" }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <header className="dialog__head">
              <h2>{t("reports.previewTitle")}</h2>
              <button
                type="button"
                aria-label={t("common.close")}
                onClick={() => setReadingScanId(null)}
              >
                ×
              </button>
            </header>
            <div className="dialog__body">
              <div className="reports__preview-scaler" ref={previewBoxRef}>
                <iframe
                  className="reports__preview-frame"
                  data-tour="report-preview"
                  title={t("reports.previewTitle")}
                  sandbox=""
                  srcDoc={REPORT_PREVIEW_CSS + readingContent}
                  style={{
                    width: A4_PX,
                    height: Math.max(1, previewBox.h / previewScale),
                    transform: `scale(${previewScale})`,
                    transformOrigin: "top left",
                  }}
                />
              </div>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </AppShell>
  );
}

function textToHtml(text: string): string {
  const escaped = text.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });
  return `<p>${escaped}</p>`;
}
